/**
 * PROTOTYPE. Maps the real page state onto the variant model. The page state
 * hook is untouched; this only reads what it already returns.
 */
import { isDesktopDiagnosticsTab } from "../DiagnosticsDesktopTabs.logic";
import type { useDiagnosticsPanelState } from "../useDiagnosticsPanelState";
import {
  buildCloudModel,
  buildDesktopModel,
  buildServerModel,
  type DiagnosticsPrototypeModel,
  type PrototypeEvent,
} from "./diagnosticsPrototypeModel";

type PanelState = ReturnType<typeof useDiagnosticsPanelState>;

function eventsFromState(state: PanelState): ReadonlyArray<PrototypeEvent> {
  const events: PrototypeEvent[] = [];
  if (state.canonical.error) {
    events.push({
      at: null,
      title: "Diagnostics read failed",
      detail: state.canonical.error,
      tone: "error",
    });
  }
  const readiness = state.cloud.diagnostics.readinessEvent;
  if (readiness) {
    events.push({
      at: null,
      title: `${readiness.label}: Codex delivery blocked`,
      detail: readiness.code.replaceAll("_", " ").toLowerCase(),
      tone: "error",
    });
  }
  for (const event of state.cloud.runtimeErrors) {
    events.push({
      at: event.at,
      title: `${event.label}: connection error`,
      detail: event.error,
      tone: "error",
    });
  }
  return events;
}

export function diagnosticsPrototypeModelFromState(
  state: PanelState,
): DiagnosticsPrototypeModel | null {
  const events = eventsFromState(state);
  const isPlatformAdmin = state.cloud.isPlatformAdmin;
  const tab = state.tabs.activeTab === "events" ? state.activeTarget?.tab : state.tabs.activeTab;
  if (tab === "server") {
    return buildServerModel({
      target: state.activeTarget,
      label: state.server.label,
      inventory: state.server.diagnostics.inventory,
      error: state.server.diagnostics.inventoryError,
      events,
      isPlatformAdmin,
    });
  }
  if (tab !== undefined && isDesktopDiagnosticsTab(tab)) {
    const desktop = state.topology.desktopTargets.find((target) => target.tabId === tab);
    return desktop
      ? buildDesktopModel({
          target: state.activeTarget,
          desktop,
          snapshot: state.canonical.snapshot,
          events,
          isPlatformAdmin,
        })
      : null;
  }
  const runtime = state.cloud.diagnostics.selectedRuntime;
  if (!runtime) return null;
  return buildCloudModel({
    target: state.activeTarget,
    runtime,
    snapshot: state.canonical.snapshot,
    receipt: state.cloud.lifecycleReceipt,
    authority: state.cloud.providerAuthority,
    readiness: state.cloud.diagnostics.readiness,
    inventory: state.cloud.inventory.inventory,
    events,
    isPlatformAdmin,
  });
}
